import { ReactNode, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { usePrevious } from "helpers/reactHooksUtils";
import { selectUser } from "modules/auth/redux/selectors";
import { IEvent } from "modules/event/types";
import useQueryParams from "modules/queryParams/hooks/useQueryParams";
import UserDetailsToRegister from "./UserDetailsToRegister";
import { UserNameToRegister } from "./UserNameToRegister";
import { UserInputContainer } from "./UserInputContainer";

interface AuthToRegisterToEventProps {
  event: IEvent;
  onComplete: (authCompleted: boolean) => void;
  initialName?: string;
  initialEmail?: string;
  customLoading?: ReactNode;
}

const AuthToRegisterToEvent = ({
  event,
  onComplete,
  initialName,
  initialEmail,
  customLoading,
}: AuthToRegisterToEventProps) => {
  const [email, setEmail] = useState({
    error: false,
    value: initialEmail ?? "",
    message: "",
  });
  const [showUserDetailsForm, setShowUserDetailsForm] = useState(false);
  const [showUserNameForm, setShowUserNameForm] = useState(false);
  const [goToLogin, setGoToLogin] = useState(false);
  const user = useSelector(selectUser);
  const prevUser = usePrevious(user);
  const { email: paramEmail } = useQueryParams();

  /**
   * If user has successfully logged in, then go to success page,
   * if user has clicked back button, then show email form again
   * @param authCompleted boolean
   */
  const handleAuthComplete = useCallback(
    (authCompleted: boolean) => {
      if (authCompleted) {
        onComplete(authCompleted);
      } else {
        setShowUserDetailsForm(false);
      }
    },
    [setShowUserDetailsForm, onComplete],
  );

  // Reset signup flag only after getting user
  useEffect(() => {
    if (!prevUser && user) {
      handleAuthComplete(true);
    }
  }, [handleAuthComplete, prevUser, user]);

  useEffect(() => {
    if (paramEmail) {
      setEmail({ ...email, value: `${paramEmail}`, message: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (showUserDetailsForm) {
    return (
      <UserDetailsToRegister
        event={event}
        initialName={initialName}
        email={email.value}
        onComplete={handleAuthComplete}
        login={goToLogin}
        customLoading={customLoading}
      />
    );
  }
  if (showUserNameForm) {
    return <UserNameToRegister setShowUserNameForm={setShowUserNameForm} />;
  }
  return (
    <UserInputContainer
      event={event}
      email={email}
      setEmail={setEmail}
      setGoToLogin={setGoToLogin}
      setShowUserDetailsForm={setShowUserDetailsForm}
      setShowUserNameForm={setShowUserNameForm}
    />
  );
};

export default AuthToRegisterToEvent;
